import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Minus, Plus, Trash2, ShoppingCart, Loader2, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { resolveImg } from '../lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartSheet = ({ open, onOpenChange }) => {
  const { items, removeItem, setQty, total } = useCart();
  const [stage, setStage] = useState('cart'); // cart | checkout
  const [form, setForm] = useState({ name: '', email: '', address: '', city: '', state: '', zip: '' });
  const [processing, setProcessing] = useState(false);

  const reset = () => setStage('cart');

  const handleCheckout = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url; // redirect to Stripe
      } else {
        throw new Error('No checkout URL');
      }
    } catch (err) {
      toast.error('Could not start checkout. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <SheetContent className="bg-black border-l border-gold/30 text-white w-full sm:max-w-md flex flex-col" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            {stage === 'checkout' && (
              <button onClick={reset} className="text-gold" data-testid="cart-back-button">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <ShoppingCart className="w-5 h-5 text-gold" />
            {stage === 'cart' ? 'Your Cart' : 'Checkout'}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-white/50" data-testid="cart-empty">
            <ShoppingCart className="w-12 h-12 mb-4 opacity-40" />
            Your cart is empty.
          </div>
        ) : stage === 'cart' ? (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3" data-testid={`cart-item-${i.product_id}`}>
                  <img src={resolveImg(i.image)} alt={i.name} className="w-16 h-16 rounded object-cover bg-white/5" />
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{i.name}</p>
                    <p className="text-gold text-sm">${i.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="w-6 h-6 rounded bg-white/10 flex items-center justify-center hover:bg-white/20" data-testid={`cart-dec-${i.product_id}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-5 text-center">{i.quantity}</span>
                      <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="w-6 h-6 rounded bg-white/10 flex items-center justify-center hover:bg-white/20" data-testid={`cart-inc-${i.product_id}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeItem(i.product_id)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`cart-remove-${i.product_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Separator className="bg-white/10" />
            <div className="py-4 space-y-4">
              <div className="flex items-center justify-between text-lg">
                <span className="text-white/70">Total</span>
                <span className="text-gold font-bold" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <Button
                className="w-full bg-gold text-black hover:bg-gold/90 font-semibold"
                onClick={() => setStage('checkout')}
                data-testid="proceed-checkout-button"
              >
                Proceed to Checkout
              </Button>
              <p className="text-white/40 text-xs text-center">Secure payment powered by Stripe</p>
            </div>
          </>
        ) : (
          <form onSubmit={handleCheckout} className="flex-1 flex flex-col py-4" data-testid="checkout-form">
            <div className="space-y-3 flex-1 overflow-y-auto">
              <Input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="checkout-name" />
              <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="checkout-email" />
              <Input required placeholder="Shipping Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="checkout-address" />
              <div className="grid grid-cols-2 gap-3">
                <Input required placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="checkout-city" />
                <Input required placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="checkout-state" />
              </div>
              <Input required placeholder="ZIP Code" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="checkout-zip" />
            </div>
            <Separator className="bg-white/10 my-4" />
            <div className="flex items-center justify-between text-lg mb-4">
              <span className="text-white/70">Total</span>
              <span className="text-gold font-bold">${total.toFixed(2)}</span>
            </div>
            <Button type="submit" disabled={processing} className="w-full bg-gold text-black hover:bg-gold/90 font-semibold" data-testid="pay-button">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {processing ? 'Redirecting…' : 'Pay with Card'}
            </Button>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
